import type { ReactNode } from 'react';
import { ArrowUpRight, Star, type LucideIcon } from 'lucide-react';
import { type RosSystem } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn, dateOnly } from '@/lib/utils';
import { certaintyLabel, rosSystemLabel } from './labels';
import { Badge, StatusBadge } from '@/components/ui/primitives';
import { LastVisitCard } from '../../patients/PreviousVisits';
import { examinationFields, historyFields, planFields, rosRecorded, type EmrNavId, type EmrSection, type EmrSectionId, type NoteFieldDef } from './sections';
import { noteText, type Encounter } from './types';
import { OrderSummaryList } from './orders/OrderSummaryList';
import { orderLineCount } from './orders/summary';
import type { OrderKind } from './orders/types';

const ORDER_CARDS: readonly { id: EmrSectionId; kind: OrderKind; empty: string }[] = [
  { id: 'lab_order', kind: 'lab', empty: 'emr_empty_lab_order' },
  { id: 'radiology_order', kind: 'radiology', empty: 'emr_empty_radiology_order' },
  { id: 'procedure_order', kind: 'procedure', empty: 'emr_empty_procedure_order' },
];

function SummaryCard({
  section,
  onOpen,
  filled,
  emptyText,
  children,
  className,
}: {
  section: EmrSection | undefined;
  onOpen: (id: EmrNavId) => void;
  filled: boolean;
  emptyText: string;
  children?: ReactNode;
  className?: string;
}) {
  if (!section) return null;
  const Icon: LucideIcon = section.icon;
  return (
    <section className={cn('group flex flex-col rounded-2xl border bg-card shadow-sm transition-shadow hover:shadow-md', className)}>
      <button
        type="button"
        onClick={() => onOpen(section.id)}
        className="flex items-center gap-2.5 rounded-t-2xl border-b px-4 py-3 text-start focus-ring"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary" aria-hidden>
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{section.label}</span>
        {section.count !== undefined && section.count > 0 && <Badge variant="muted">{section.count}</Badge>}
        <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl:-scale-x-100" aria-hidden />
      </button>
      <div className="flex-1 px-4 py-3 text-sm">
        {filled ? (
          children
        ) : (
          <button type="button" onClick={() => onOpen(section.id)} className="w-full rounded-lg border border-dashed px-3 py-2.5 text-start text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-ring">
            {emptyText}
          </button>
        )}
      </div>
    </section>
  );
}

function NoteSnippets({ encounter, fields }: { encounter: Encounter; fields: NoteFieldDef[] }) {
  const filled = fields.filter((field) => noteText(encounter, field.key).trim());
  return (
    <dl className="space-y-2">
      {filled.map((field) => (
        <div key={field.key}>
          <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{field.label}</dt>
          <dd className="line-clamp-3 whitespace-pre-line">{noteText(encounter, field.key)}</dd>
        </div>
      ))}
    </dl>
  );
}

function hasNotes(encounter: Encounter, fields: NoteFieldDef[]): boolean {
  return fields.some((field) => noteText(encounter, field.key).trim() !== '');
}

export function EmrOverview({ encounter, sections, onOpen }: { encounter: Encounter; sections: EmrSection[]; onOpen: (id: EmrNavId) => void }) {
  const find = (id: EmrSectionId) => sections.find((section) => section.id === id);
  const rosFindings = (encounter.notes?.rosFindings ?? {}) as Partial<Record<RosSystem, string>>;
  const abnormalSystems = (Object.keys(rosFindings) as RosSystem[]).filter((system) => rosFindings[system] === 'abnormal');
  const complaintFilled = Boolean(encounter.chiefComplaint?.trim()) || hasNotes(encounter, historyFields());
  const examFilled = hasNotes(encounter, examinationFields()) || rosRecorded(encounter);
  const planFilled = hasNotes(encounter, planFields()) || Boolean(encounter.followUpDate);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <SummaryCard section={find('complaint')} onOpen={onOpen} filled={complaintFilled} emptyText={tr('opd', 'emr_empty_complaint')} className="lg:col-span-2">
          {encounter.chiefComplaint && (
            <p className="mb-2 text-base font-medium">
              {encounter.chiefComplaint}
              {encounter.complaintDurationValue ? (
                <span className="ms-2 text-sm font-normal text-muted-foreground">
                  · {tr('opd', 'emr_for_x', { value: `${encounter.complaintDurationValue} ${encounter.complaintDurationUnit ?? ''}`.trim() })}
                </span>
              ) : null}
            </p>
          )}
          <NoteSnippets encounter={encounter} fields={historyFields()} />
        </SummaryCard>

        <SummaryCard section={find('examination')} onOpen={onOpen} filled={examFilled} emptyText={tr('opd', 'emr_empty_examination')}>
          <NoteSnippets encounter={encounter} fields={examinationFields()} />
          {abnormalSystems.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{tr('opd', 'emr_abnormal_systems')}</span>
              {abnormalSystems.map((system) => (
                <Badge key={system} variant="destructive">
                  {rosSystemLabel(system)}
                </Badge>
              ))}
            </div>
          )}
          {rosRecorded(encounter) && abnormalSystems.length === 0 && <p className="mt-2 text-xs text-success">{tr('opd', 'emr_ros_all_normal')}</p>}
        </SummaryCard>

        <SummaryCard section={find('diagnosis')} onOpen={onOpen} filled={encounter.diagnoses.length > 0} emptyText={tr('opd', 'emr_empty_diagnosis')}>
          <ul className="space-y-1.5">
            {encounter.diagnoses.map((diagnosis) => (
              <li key={diagnosis.id} className="flex items-start gap-2">
                {diagnosis.isPrimary ? (
                  <Star className="mt-0.5 h-3.5 w-3.5 shrink-0 fill-warning text-warning" aria-label={tr('opd', 'primary')} />
                ) : (
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" aria-hidden />
                )}
                <span className="min-w-0 flex-1">
                  <span className="font-mono text-xs text-primary">{diagnosis.icdCode}</span> {diagnosis.icdTitle}
                </span>
                <Badge variant={diagnosis.certainty === 'confirmed' ? 'success' : 'muted'}>{certaintyLabel(diagnosis.certainty)}</Badge>
              </li>
            ))}
          </ul>
        </SummaryCard>

        <SummaryCard section={find('plan')} onOpen={onOpen} filled={planFilled} emptyText={tr('opd', 'emr_empty_plan')}>
          <NoteSnippets encounter={encounter} fields={planFields()} />
          {encounter.followUpDate && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {tr('opd', 'emr_follow_up_on_x', { date: dateOnly(encounter.followUpDate) })}
            </p>
          )}
        </SummaryCard>

        <SummaryCard section={find('prescription')} onOpen={onOpen} filled={encounter.prescriptions.length > 0} emptyText={tr('opd', 'emr_empty_prescription')}>
          <ul className="space-y-2">
            {encounter.prescriptions.map((prescription) => (
              <li key={prescription.id} className="rounded-lg border px-3 py-2">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">{dateOnly(prescription.issuedAt)}</span>
                  <StatusBadge status={prescription.status} />
                </div>
                <ul className="space-y-0.5">
                  {prescription.items.map((item, index) => (
                    <li key={item.id ?? index}>
                      <span className="font-medium">{item.drugName}</span>
                      <span className="text-muted-foreground"> · {[item.dose, item.frequency].filter(Boolean).join(' · ')}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </SummaryCard>

        {ORDER_CARDS.map(({ id, kind, empty }) => (
          <SummaryCard key={id} section={find(id)} onOpen={onOpen} filled={orderLineCount(encounter, kind) > 0} emptyText={tr('opd', empty)}>
            <OrderSummaryList encounter={encounter} kind={kind} />
          </SummaryCard>
        ))}
      </div>

      <LastVisitCard patientId={encounter.patient.id} excludeEncounterId={encounter.id} onSeeAll={() => onOpen('history')} />
    </div>
  );
}
